import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CouponApi } from 'api';
import type {
  Coupon,
  MapCreateCouponRequest,
  UpdateMapMenuPolicy,
  MapCreateProductCouponRequest,
} from 'api/methods/CouponApi';

/**
 * [Coupon] Query Key 생성
 * --
 */
export const couponQueryKeys = createQueryKeys('coupon', {
  getCoupon: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getCoupons: (params?: { [key: string]: string | number }) => [params],
  getMapCoupons: (
    params?: { [key: string]: string | number },
    key?: string
  ) => [params, key],
  getMapProductCoupons: (
    params?: { [key: string]: string | number },
    key?: string
  ) => [params, key],
});

/**
 * [Coupon] 쿠폰 상세 조회
 * --
 */
export const useGetCoupon = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: couponQueryKeys.getCoupon(idx, params).queryKey,
    queryFn: () => CouponApi.getCoupon(idx, params),
  });

  return data;
};

/**
 * [Coupon] 쿠폰 전체 조회
 * --
 */
export const useGetCoupons = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: couponQueryKeys.getCoupons(params).queryKey,
    queryFn: () => CouponApi.getCoupons(params),
    ...options,
  });

  return data;
};

/**
 * [Coupon] 쿠폰 생성
 * --
 */
export const useCreateCoupon = (body: Coupon) => {
  const { mutateAsync: createCoupon, ...result } = useMutation({
    mutationFn: (variables: Coupon) => CouponApi.createCoupon(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createCoupon, ...result };
};
/**
 * [Coupon] 쿠폰 일괄 맵핑 생성
 * --
 */
export const useMapCreateCoupon = (body: MapCreateCouponRequest) => {
  const { mutateAsync: mapCreateCoupon, ...result } = useMutation({
    mutationFn: (variables: MapCreateCouponRequest) =>
      CouponApi.mapCreateCoupon(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { mapCreateCoupon, ...result };
};

/**
 * [Coupon] 쿠폰 일괄 맵핑 생성
 * --
 */
export const useMapCreateProductCoupon = () => {
  const { mutateAsync: mapCreateProductCoupon, ...result } = useMutation({
    mutationFn: (variables: MapCreateProductCouponRequest) =>
      CouponApi.mapCreateProductCoupon(variables),
  });

  return { mapCreateProductCoupon, ...result };
};

/**
 * [Coupon] 쿠폰 전체 조회
 * --
 */
export const useMapGetCoupons = (params?: {
  [key: string]: string | number;
}) => {
  const data = useQuery({
    queryKey: couponQueryKeys.getMapCoupons(params, 'map-menu').queryKey,
    queryFn: () => CouponApi.getMapCoupons(params),
  });

  return data;
};

/**
 * [Coupon] 쿠폰 - 상품 전체 조회
 * --
 */
export const useGetMapProductCoupons = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: couponQueryKeys.getMapProductCoupons(params, 'map-product')
      .queryKey,
    queryFn: () => CouponApi.getMapProductCoupons(params),
    ...options,
  });

  return data;
};

/**
 * [Coupon] 쿠폰 수정
 * --
 */
export const useUpdateCoupon = () => {
  const { mutateAsync: updateCoupon, ...result } = useMutation({
    mutationFn: (variables: Coupon) => CouponApi.updateCoupon(variables),
  });

  return { updateCoupon, ...result };
};

/**
 * [Coupon] 쿠폰 메뉴 정책 맵핑 수정
 * --
 */
export const useBulkUpdateMapMenuPolicy = () => {
  const { mutateAsync: updateBulkMapMenuPolicy, ...result } = useMutation({
    mutationFn: (variables: UpdateMapMenuPolicy[]) =>
      CouponApi.updateBulkMapMenuPolicy(variables),
  });

  return { updateBulkMapMenuPolicy, ...result };
};

/**
 * [Coupon] 쿠폰 삭제
 * --
 */
export const useDeleteCoupon = () => {
  const { mutateAsync: deleteCoupon, ...result } = useMutation({
    mutationFn: (variables: number) => CouponApi.deleteCoupon(variables),
  });

  return { deleteCoupon, ...result };
};
